'use client'

import { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import type { HTMLProps } from 'react'
import clsx from 'classnames'
import useEmblaCarousel from 'embla-carousel-react'
import { AnimatePresence, motion } from 'framer-motion'
import { useLocale } from 'use-intl'
import { useTranslations } from 'next-intl'
import { useAppearance, variantsDefault } from '@/shared/utils'
import { Text } from '@/shared/ui'
import { ArrowXAxisIcon } from '@/shared/icons/arrow-x-axis-icon'
import { variantsCards } from '@/shared/constants/variants'
import getTasks from '@/actions/getTasks'
import { localeMap } from '@/shared/utils/directus'
import getWorkgroups from '@/actions/getWorkgroups'
import { MotionLink } from '@/shared/ui/link'
import style from './about-cards.module.scss'

interface CardsProps extends HTMLProps<HTMLDivElement> {
  loaded?: boolean
}

export function AboutCards({ className, loaded }: CardsProps) {
  const [activeTab, setActiveTab] = useState<string>('workgroups')
  const [cards, setCards] = useState<any>()
  const [tasks, setTasks] = useState<any>([])
  const [workgroups, setWorkgroups] = useState<any>([])
  const currentLocale = useLocale()
  const categories = useTranslations('AboutTabs')

  const [emblaRef] = useEmblaCarousel({
    align: 'start',
    container: `.${style.cards}`,
    breakpoints: {
      '(min-width: 1024px)': { active: false },
      '(max-width: 768px)': { align: 'center' },
    },
  })

  const { ref: tabsRef, animate: tabsAnimate } = useAppearance()
  const { ref: cardsRef, animate: cardsAnimate } = useAppearance()

  useEffect(() => {
    if (!currentLocale) return
    getTasks({ locale: localeMap[currentLocale] }).then(res => {
      setTasks(res.map(({ image, title }) => ({ ...image, title, category: 'tasks' })))
    })
    getWorkgroups({ locale: localeMap[currentLocale] }).then(res => {
      setWorkgroups(res.map(({ image, name, slug }) => ({ ...image, title: name, category: 'workgroups', slug })))
    })
  }, [currentLocale])

  useEffect(() => {
    if (!currentLocale) return

    setCards([...tasks, ...workgroups])
  }, [tasks, workgroups, currentLocale])

  // const tasks = tasksData?.map(task => ({ ...task, category: 'tasks' }))
  const cardList = useMemo(
    () =>
      cards?.filter(({ category }: any) => {
        return category === activeTab
      }),
    [cards, activeTab]
  )

  return (
    <motion.section id="cards" className={clsx(className, style.container, { [style.loaded]: loaded })}>
      <motion.ul
        id="work-group"
        ref={tabsRef}
        initial="hidden"
        animate={tabsAnimate}
        variants={variantsDefault}
        className={style.tabs}
      >
        <button
          key={'workgroups'}
          onClick={() => setActiveTab('workgroups')}
          className={clsx('text text_type_d', style.tab, {
            [style.active]: activeTab === 'workgroups',
          })}
        >
          {categories('workgroups')}
        </button>
        <button
          key={'tasks'}
          onClick={() => setActiveTab('tasks')}
          className={clsx('text text_type_d', style.tab, {
            [style.active]: activeTab === 'tasks',
          })}
        >
          {categories('tasks')}
        </button>
      </motion.ul>
      <div className={style.cardsWrapper} ref={emblaRef}>
        <motion.ul
          className={style.cards}
          ref={cardsRef}
          initial="hidden"
          animate={cardsAnimate}
          variants={variantsDefault}
        >
          <AnimatePresence mode="popLayout">
            {cardList?.map(({ category, title, url, slug }: any, index: any) => (
              <MotionLink
                className={clsx(style.card, {
                  [style.linkTab]: activeTab === 'workgroups',
                  [style.linkDisable]: activeTab === 'tasks',
                })}
                href={activeTab === 'workgroups' ? `/workgroup/${slug}` : ''}
                key={category + index}
                variants={variantsCards}
                custom={index}
              >
                <Text className={style.desc} type="h3">
                  {title}
                </Text>
                <div className={style.imagesWrapper}>
                  <div className={style.img}>
                    <Image src={url} alt={title} fill style={{ objectFit: 'cover' }} />
                  </div>
                  {activeTab === 'workgroups' && <ArrowXAxisIcon direction="next" className={style.imgArrow} />}
                </div>
              </MotionLink>
            ))}
          </AnimatePresence>
        </motion.ul>
      </div>
    </motion.section>
  )
}
